import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AppLocation } from '../../../core/models/interface/geo';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { buildRouteEnding } from '../../../core/pure-functions/navigation/buyflow-vessel-route-builder/buyflow-vessel-route-builder';
import { getFadeInTransition } from '../../../core/pure-functions/animations/ng-animations-provider';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MapStyles } from './map-styles';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { VesselAvailability } from 'anchor-shared/lib/model/entity/vesselAvailability';
import { CharterType } from 'anchor-shared/lib/model/enum/charter-type';
import ZoomControlOptions = google.maps.ZoomControlOptions;
import LatLngBounds = google.maps.LatLngBounds;
import MarkerOptions = google.maps.MarkerOptions;
import MapOptions = google.maps.MapOptions;
import MapTypeStyle = google.maps.MapTypeStyle;
import LatLngLiteral = google.maps.LatLngLiteral;

export const GREEN_MARKER =
  'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/green-way-marker.png';
export const RED_MARKER =
  'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/red-way-marker.png';

@Component({
  selector: 'app-buyflow-map',
  templateUrl: './app-buyflow-map.component.html',
  styleUrls: ['./app-buyflow-map.component.scss'],
  animations: [getFadeInTransition(500)],
})
export class AppBuflowMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;

  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;

  @Input()
  location: AppLocation;

  /**
   * When updating this list, we need to also update the markers.
   * Use the `manuallyUpdateVesselList` in order to do this.
   */
  @Input()
  vessels: VesselAvailability[] | Vessel[];

  @Input()
  allowFullScreen: boolean;

  @Input()
  customActionConfig: CustomMapActionConfig;

  @Input()
  zoomControlOptions: ZoomControlOptions;

  @Input()
  selectedWaterBodyId: number;

  @Input()
  manuallyUpdateVesselList: BehaviorSubject<{ vessels: VesselAvailability[] }>;

  @Input()
  buyflowMapInitialization: BuyflowMapInitialization = null;

  @Input()
  charterType: CharterType = CharterType.DayCharter;

  @Input()
  termPricePerDay: number;

  /**
   * True by default.
   * Setting to false enables map bounds updates to be emitted in a debounced experience.
   */
  @Input()
  searchThisAreaEnabled = true;

  @Input()
  gestureHandling: 'cooperative' | 'greedy' | 'none' | 'auto' = 'cooperative';

  @Output()
  customMapActionEmit = new EventEmitter();

  @Output()
  boundsUpdateEmit = new EventEmitter<MapBoundsAndZoom>();
  icon: any;
  markers: any[] = [];
  latestBounds: LatLngBounds;
  currentInfoWindow: MapInfoWindow = null;
  previousInfoWindow: MapInfoWindow = null;

  center: LatLngLiteral = {
    lat: null,
    lng: null,
  };

  currentMarker: any = null;
  previousMarker: MapMarker = null;
  mapStyles: MapTypeStyle[] = MapStyles.DEFAULT_MAP_STYLES;
  zoom: number;
  boundsTimeout: any = null;
  private subs: any[] = [];
  fitBounds = true;
  latitude: number;
  longitude: number;
  private mapSettled: boolean;
  showSearchSpinner: boolean;
  private currentMarkerVesselIds: number[] = [];
  selectedVesselMarker: any;
  markerClustererImagePath = '../../../assets/markerClustererIcons/m';
  markerOptions: MarkerOptions = {
    draggable: false,
    icon: {
      url: RED_MARKER,
    },
  };
  CharterType = CharterType;

  mapOptions: MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: this.mapStyles,
    minZoom: BUYFLOW_MAP_MIN,
    gestureHandling: this.gestureHandling,
  };

  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    public route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.initWatchers();
    this.setDefaults();
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.zoom = 0;
    this.fitBounds = true;
    setTimeout(() => {
      if (this.location.zoom > 0) {
        this.zoom = this.location.zoom;
      }
      this.createMarkers();
      this.addCustomControls();
    }, 1000);

    if (this.buyflowMapInitialization) {
      this.center.lat = this.buyflowMapInitialization.center.latitude;
      this.center.lng = this.buyflowMapInitialization.center.longitude;
      this.zoom = this.buyflowMapInitialization.zoom;
      this.fitBounds = false;
    } else if (this.route.snapshot.queryParamMap.get('latitude')) {
      (this.center = {
        lat: parseFloat(this.route.snapshot.queryParamMap.get('latitude')),
        lng: parseFloat(this.route.snapshot.queryParamMap.get('longitude')),
      }),
        (this.zoom = parseFloat(this.route.snapshot.queryParamMap.get('zoom'))),
        (this.fitBounds = false);
    } else if (this.location) {
      this.center.lat = this.location.position.lat;
      this.center.lng = this.location.position.lng;
      this.zoom = this.location.zoom;
      this.fitBounds = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.termPricePerDay || changes.charterType) &&
      this.markers.length
    ) {
      this.markers = [];
      this.currentMarkerVesselIds = [];

      this.createMarkers();
    }
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  markerClickEvent(
    marker: MapMarker,
    index: number,
    infoWindow: MapInfoWindow,
  ): void {
    this.selectedVesselMarker = this.markers[index];
    this.syncInfoWindow(infoWindow);
    this.syncMarkers(marker);
    this.infoWindow.open(marker);
  }

  mapClick(): void {
    if (this.previousInfoWindow) {
      this.previousInfoWindow.close();
    }
    this.resetMarkers();
  }

  checkAvailability(vessel: Vessel): void {
    let vesselUrl = this.selectedWaterBodyId
      ? 'vessels/yacht-and-boat-rentals/' +
        buildRouteEnding(vessel) +
        '?waterBody=' +
        this.selectedWaterBodyId
      : 'vessels/yacht-and-boat-rentals/' + buildRouteEnding(vessel);
    window.open(vesselUrl, '_blank');
  }

  onBoundChangeEvent(): void {
    this.latestBounds = null;
    if (this.mapSettled) {
      if (this.boundsTimeout) {
        clearTimeout(this.boundsTimeout);
      }
      this.boundsTimeout = setTimeout(() => {
        if (this.searchThisAreaEnabled) {
          this.showSearchThisArea();
        } else {
          this.emitBoundsEvent();
        }
      }, 1000);
    } else {
      this.emitBoundsEvent();
    }
    this.mapSettled = true;
  }

  searchThisAreaClick(): void {
    this.showSearchSpinner = true;
    this.emitBoundsEvent();
  }

  private syncMarkers(marker: MapMarker): void {
    if (this.previousMarker) {
      this.currentMarker = marker;
      this.resetMarkers();
    }
    this.previousMarker = marker;
  }

  private syncInfoWindow(infoWindow: MapInfoWindow) {
    if (this.previousInfoWindow) {
      this.currentInfoWindow = infoWindow;
      this.previousInfoWindow.close();
    }
    this.previousInfoWindow = infoWindow;
  }

  /**
   * Not a great solution.  Should probably keep track of the
   * previous marker index instead of resetting all.
   */
  private resetMarkers(): void {
    this.markers.forEach((marker) => {
      marker.icon = {
        url: RED_MARKER,
        scaledSize: {
          height: 25,
          width: this.charterType === CharterType.TermCharter ? 85 : 55,
        },
      };
    });
  }

  private createMarkers(): void {
    const newVesselIds: number[] = [];
    let zIndex = 0;
    for (let vessel of this.vessels) {
      let parkingLat,
        parkingLong,
        halfDayPrice,
        fullDayPrice,
        termPricePerDay,
        featuredImage,
        vesselName,
        lengthFt,
        make,
        fuelIncluded = null;

      vessel = vessel as VesselAvailability;
      termPricePerDay = vessel.termPricePerDay || this.termPricePerDay;
      let vesselId = null;

      if (vessel.parkingLocation) {
        parkingLat = vessel.parkingLocation.coordinates[0];
        parkingLong = vessel.parkingLocation.coordinates[1];
        vesselId = vessel.vesselId;
        halfDayPrice = vessel.halfDayPrice;
        fullDayPrice = vessel.fullDayPrice;
        termPricePerDay = vessel.termPricePerDay;
        featuredImage = vessel.featuredImage;
        vesselName = vessel.name;
        lengthFt = vessel.length;
        make = vessel.make;
        fuelIncluded = vessel.fuelIncluded;
        newVesselIds.push(vessel.vesselId);
      } else {
        // This is for the boat list page map, this page uses the vessel rather than
        // the a VesselAvailability object
        vessel = vessel as unknown as Vessel;
        parkingLat = vessel.mooringLocation.parkingLat;
        parkingLong = vessel.mooringLocation.parkingLong;
        vesselId = vessel.id;
        halfDayPrice = vessel.halfDayListPrice;
        fullDayPrice = vessel.fullDayListPrice;
        termPricePerDay = termPricePerDay;
        featuredImage = vessel.primaryPhotoAbsPath;
        vesselName = vessel.name;
        lengthFt = vessel.lengthFt;
        make = vessel.make;
        fuelIncluded = vessel.fuelIncluded;
        newVesselIds.push(vessel.id);
      }
      if (!this.currentMarkerVesselIds.includes(vesselId)) {
        this.currentMarkerVesselIds.push(vesselId);
        for (let i = 0; i < this.markers.length; i++) {
          if (
            this.markers[i].position.lat === parkingLat &&
            this.markers[i].position.lng === parkingLong &&
            parkingLat &&
            parkingLong
          ) {
            parkingLat += (Math.random() - 0.5) / 1500;
            parkingLong += (Math.random() - 0.5) / 1500;
          }
        }
        if (vessel && parkingLat && parkingLong) {
          this.markers.push(
            this.createNewMarker(
              parkingLat,
              parkingLong,
              halfDayPrice,
              fullDayPrice,
              termPricePerDay,
              featuredImage,
              vesselName,
              lengthFt,
              make,
              vesselId,
              fuelIncluded,
              zIndex,
            ),
          );
          zIndex++;
        }
      }
    }

    this.currentMarkerVesselIds = newVesselIds;

    this.markers = this.markers.filter((marker) => {
      return newVesselIds.includes(marker.id);
    });
  }

  private createNewMarker(
    parkingLat: number,
    parkingLong: number,
    halfDayPrice: number,
    fullDayPrice: number,
    termPricePerDay: number,
    featuredImage: string,
    vesselName: string,
    lengthFt: number,
    make: string,
    vesselId: number,
    fuelIncluded: boolean,
    zIndex: number,
  ) {
    return {
      position: {
        lat: parkingLat,
        lng: parkingLong,
      },
      options: {
        zIndex: zIndex,
        icon: {
          url: RED_MARKER,
          scaledSize: {
            height: 25,
            width: this.charterType === CharterType.TermCharter ? 85 : 55,
          },
        },
      },
      label: {
        color: '#ffffff',
        fontSize: '16px',
        fontWeight: '500',
        text:
          '$' +
          Math.floor(
            this.getDisplayPrice(fullDayPrice, halfDayPrice, termPricePerDay),
          ).toString() +
          `${this.charterType === CharterType.TermCharter ? '/day' : ''}`,
      },
      image: featuredImage,
      displayText: vesselName,
      halfDayListPrice: halfDayPrice,
      fullDayListPrice: fullDayPrice,
      termPricePerDay,
      lengthFt: lengthFt,
      make: make,
      id: vesselId,
      fuelIncluded: fuelIncluded,
    };
  }

  private getDisplayPrice(fullDayPrice, halfDayPrice, termPricePerDay) {
    if (this.charterType === CharterType.DayCharter) {
      return halfDayPrice ? halfDayPrice : fullDayPrice;
    } else {
      return termPricePerDay;
    }
  }

  private initWatchers(): void {
    if (this.manuallyUpdateVesselList) {
      const manuallyUpdateVesselSub = this.manuallyUpdateVesselList.subscribe(
        (newVesselList) => {
          if (newVesselList && newVesselList.vessels) {
            this.vessels = newVesselList.vessels;
            this.createMarkers();
            if (document.getElementById('search-this-area')) {
              this.hideSearchThisArea();
            }
            this.showSearchSpinner = false;
          }
        },
      );
      this.subs.push(manuallyUpdateVesselSub);
    }
  }

  private setDefaults(): void {
    if (!this.zoomControlOptions) {
      this.zoomControlOptions = {
        position: 6,
      };
    }
    this.mapOptions.zoomControlOptions = this.zoomControlOptions;
  }

  private hideSearchThisArea(): void {
    if (document.getElementById('search-this-area')) {
      document.getElementById('search-this-area').style.opacity = '0';
    }
  }

  private showSearchThisArea(): void {
    if (document.getElementById('search-this-area')) {
      document.getElementById('search-this-area').style.opacity = '1';
    }
  }

  private emitBoundsEvent(): void {
    let bounds = this.map.getBounds();
    const mapBounds: MapBounds = {
      northEastLat: bounds.getNorthEast().lat(),
      northEastLng: bounds.getNorthEast().lng(),
      southWestLat: bounds.getSouthWest().lat(),
      southWestLng: bounds.getSouthWest().lng(),
    };
    const boundsAndZoom: MapBoundsAndZoom = {
      mapBounds: mapBounds,
      zoom: this.zoom,
      center: {
        latitude: this.map.getCenter().lat(),
        longitude: this.map.getCenter().lng(),
      },
    };
    this.fitBounds = false;
    this.boundsUpdateEmit.emit(boundsAndZoom);
    this.previousInfoWindow = null;
  }

  private addCustomControls() {
    if (this.customActionConfig) {
      this.map.controls[this.customActionConfig.position].push(
        document.getElementById('custom-action'),
      );
    }
    if (this.searchThisAreaEnabled && this.isBrowser) {
      const searchThisAreaButton = document.getElementById('search-this-area');
      this.map.controls[2].push(searchThisAreaButton);
    }
  }

  zoomChanged() {
    this.onBoundChangeEvent();
    this.zoom = this.map.getZoom();
  }
}

export interface CustomMapActionConfig {
  icon: string;
  position: number;
}

export interface MapBounds {
  northEastLat: number;
  northEastLng: number;
  southWestLat: number;
  southWestLng: number;
}

export interface MapBoundsAndZoom extends BuyflowMapInitialization {
  mapBounds: MapBounds;
}

export interface BuyflowMapInitialization {
  zoom?: number;
  center: LatLongPoint;
}

export interface LatLongPoint {
  latitude: number;
  longitude: number;
}

export function buyflowMapZoomIsValid(zoomValue: number): boolean {
  return zoomValue >= BUYFLOW_MAP_MIN && zoomValue <= BUYFLOW_MAP_MAX;
}

export const BUYFLOW_MAP_MAX = 18;
export const BUYFLOW_MAP_MIN = 8;
